import AuthStore from 'auth/AuthStore'
import { snackbar, snackbarError } from 'reusable/mui/snackbar/snackbar'
import Loader from 'reusable/mobx-helpers/Loader'
import { FormHandlerConfig, formHandler } from 'reusable/mobx-store-utils/form-handlers/new/form-handler'
import { signIn, signOut } from 'aws-amplify/auth'
import { makeAutoObservable } from 'mobx'
import { apiClient } from 'api/trpc/trpc-client'
import variables from 'variables/variables'
import { encryptWithPublicKey } from 'reusable/utils/challenge'
import { UserDto } from '@gap/backend/app-services/user/models/UserDto'
import { PasswordConfirmSchema } from '@gap/backend/util/validations/validation-passwords'

export default class AuthNonAdvisorSignUpStore {
   loader = new Loader(true)
   user: UserDto.withOrgs
   sp

   constructor(sp: { authStore: AuthStore, postJoinUrl?: string }) {
      this.sp = sp
      makeAutoObservable(this)
      if (variables.quickFillActive) {
         sp.authStore.fields.password = variables.quickFillPassword
         sp.authStore.fields.passwordConfirm = variables.quickFillPassword
      }
   }

   get formHandler() {
      return formHandler(this.sp.authStore.fields, {
         email: { type: 'string' },
         password: { type: 'string' },
         passwordConfirm: { type: 'string' }
      } satisfies FormHandlerConfig)
   }

   fetchUser = async () => {
      await this.loader.executeLoading(async () => {
         this.user = await apiClient.user.getMyUser.query()
         this.sp.authStore.fields.email = this.user.email
      })
   }

   ensureInviteIsPendingAcceptance = async () => {
      const inviteIsPendingAcceptance = await this.sp.authStore.checkInviteIsPendingAcceptance({ email: this.sp.authStore.fields.email })
      if (!inviteIsPendingAcceptance) {
         this.sp.authStore.redirectFromInvalidInvitation({ postJoinUrl: this.sp.postJoinUrl })
      }
   }

   signUp = async () => {
      if (this.formHandler.validate(PasswordConfirmSchema)) {
         this.loader.executeLoading(async () => {
            try {
               const { publicKey } = await apiClient.challenge.create.mutate()
               const encryptedPassword = await encryptWithPublicKey({
                  text: this.sp.authStore.fields.password,
                  publicKey
               })
               await apiClient.user.updateMyPassword.mutate({ encryptedPassword })
               await apiClient.user.acceptInvitation.mutate()

               await signOut()
               this.sp.authStore.displayPasswordResetSuccess = true

               this.sp.authStore.navigateToSignIn()
               // This signIn should be successfull as we haven't got an error, 
               // so directly set status to expected state
               this.sp.authStore.status = 'verify with mfa code'
               await signIn({
                  username: this.sp.authStore.fields.email,
                  password: this.sp.authStore.fields.password
               })
            } catch (error: unknown) {
               const isError = error instanceof Error
               if (!isError) {
                  snackbarError(`${error}`)
                  return
               }
               snackbar({ title: `${error.message ?? error}`, variant: 'error' })
            }
         })
      }
   }
}
